// src/content/products.tsx — Site officiel Vanessa
// Contenu des pages produits, tiré de ce que fait réellement l'application.
import type { ReactNode } from 'react';
import {
    MessageCircle, Mic, Camera, Brain, Sparkles, EyeOff, Flame, Award, BookOpen, Flag, MapPin, PlusCircle,
    CheckCircle2, Navigation, Banknote, ShieldCheck, Eye, Globe, Bell, Users, Layers, type LucideIcon,
} from 'lucide-react';

export interface ProductFeature {
    icon: LucideIcon;
    title: string;
    text: ReactNode;
}

export interface Product {
    slug: string;
    name: string;
    icon: LucideIcon;
    tagline: string;
    intro: string;
    status?: string; // ex. « Réservé à l'équipe pour le moment »
    features: ProductFeature[];
    stepsTitle?: string;
    steps?: { title: string; text: string }[];
    note?: string;
}

export const PRODUCTS: Product[] = [
    {
        slug: 'discuter',
        name: 'Discuter avec Vanessa',
        icon: MessageCircle,
        tagline: 'Une amie qui adore les histoires, pas un chatbot.',
        intro:
            'Écrivez-lui, envoyez-lui un vocal ou une photo. Vanessa répond avec son ton, ses humeurs et sa mémoire, et reste sérieuse quand le sujet l’exige.',
        features: [
            { icon: MessageCircle, title: 'Texte', text: 'Des réponses courtes et vivantes, comme dans une vraie conversation de chat.' },
            { icon: Mic, title: 'Vocal', text: 'Envoyez un message vocal : Vanessa le comprend et peut vous répondre à voix haute.' },
            { icon: Camera, title: 'Photo', text: 'Envoyez une image et demandez-lui ce qu’elle en pense. Elle commente la situation ou l’ambiance, jamais le physique d’une personne.' },
            { icon: Brain, title: 'Mémoire', text: 'Elle retient certaines choses pour des échanges plus naturels. Vous consultez et effacez ce qu’elle sait de vous depuis votre profil.' },
            { icon: Sparkles, title: 'Humeurs', text: 'Curieuse, moqueuse, détective, confidente : elle s’adapte au ton de la conversation, sans que vous ayez à lui demander.' },
            { icon: Layers, title: 'Connecteurs partenaires', text: 'Une conversation peut être liée à un partenaire (association, média). Une pastille l’indique et Vanessa reste factuelle sur ce sujet.' },
        ],
        stepsTitle: 'Comment ça se passe',
        steps: [
            { title: 'Ouvrez l’application', text: 'Vanessa est la première chose que vous voyez : la conversation s’ouvre toute seule.' },
            { title: 'Racontez ce que vous voulez', text: 'Une anecdote, une question, un coup de gueule. Elle relance, elle pose des questions.' },
            { title: 'Publiez si vous le souhaitez', text: 'Si votre histoire ferait un bon sujet, elle peut vous proposer de la publier sur Ça Parle. Elle ne publie jamais sans votre accord.' },
        ],
        note: 'Pour que Vanessa reste disponible pour tout le monde, le nombre d’échanges par jour et par personne est limité. Si vous l’atteignez, elle vous le dit elle-même et vous donne rendez-vous le lendemain.',
    },
    {
        slug: 'ca-parle',
        name: 'Ça Parle',
        icon: Eye,
        tagline: 'Les histoires du moment, une par écran.',
        intro:
            'Ça Parle est l’espace des ragots, révélations et témoignages. On fait défiler les histoires en plein écran, on touche pour lire la suite, on réagit et on commente.',
        features: [
            { icon: Layers, title: 'Un fil plein écran', text: 'Une histoire par écran, comme sur les applications vidéo. Le fil met en avant ce qui est récent, ce qui fait réagir, et ce qui correspond à vos centres d’intérêt.' },
            { icon: Eye, title: 'Quatre types d’histoires', text: 'Ragot, Révélation, Témoignage, Rumeur. Chaque histoire a un statut qui évolue : rumeur, en vérification, confirmée ou démentie.' },
            { icon: EyeOff, title: 'Anonymat', text: 'Publiez ou commentez sans que votre nom soit affiché aux autres. La plateforme garde une trace technique de l’auteur pour la modération en cas de signalement.' },
            { icon: Flame, title: 'Réagir et voter', text: 'Réaction rapide (chaud, mort de rire, choqué) ou vote « tu y crois ? » (100 % vrai, possible, j’y crois pas).' },
            { icon: Sparkles, title: 'Prédictions', text: 'L’auteur peut lancer une prédiction : « Que va-t-il se passer ensuite ? ». Tout le monde vote, et votre fiabilité évolue selon que vous aviez vu juste.' },
            { icon: Award, title: 'Réputation et badges', text: 'Publier, commenter et bien prédire fait monter votre niveau, votre score de réputation et votre indice de fiabilité. Des badges se débloquent automatiquement.' },
            { icon: BookOpen, title: 'Fiches références', text: 'Chaque histoire peut être rattachée à des fiches (personnes, événements, sujets) qui regroupent tout ce qui les concerne : la mémoire collective de la plateforme.' },
            { icon: Flag, title: 'Signalement et modération', text: 'Un bouton « Signaler » sur chaque contenu. L’équipe l’examine et peut le masquer, le supprimer ou sanctionner son auteur.' },
        ],
        stepsTitle: 'Publier en trois gestes',
        steps: [
            { title: 'Choisissez le type', text: 'Ragot, révélation, témoignage ou rumeur, avec une catégorie et éventuellement un pays.' },
            { title: 'Écrivez, ajoutez une photo', text: 'Un titre accrocheur et votre histoire. Vous pouvez rester anonyme.' },
            { title: 'Suivez les réactions', text: 'Ajoutez des mises à jour au fil du temps : l’histoire passe de rumeur à confirmée ou démentie.' },
        ],
    },
    {
        slug: 'ca-sert',
        name: 'Ça sert',
        icon: MapPin,
        tagline: 'Les bons plans de chez vous, sur une carte.',
        intro:
            'Ça sert regroupe les lieux, les services et les prix du moment, partagés par la communauté et vérifiés avant d’être publiés.',
        features: [
            { icon: MapPin, title: 'La carte des bons plans', text: 'Une carte interactive, avec un mode liste en bascule. Elle se recentre sur le pays que vous choisissez.' },
            { icon: PlusCircle, title: 'Ajouter un bon plan', text: 'Un lieu, un service ou un prix. Vous posez la position exacte sur la carte, ou « Me localiser ici » si vous y êtes déjà.' },
            { icon: ShieldCheck, title: 'Validation', text: 'Chaque nouveau bon plan est vérifié avant d’apparaître, pour éviter les doublons et les informations fausses. Vous suivez l’avancement dans « Mes contributions ».' },
            { icon: CheckCircle2, title: 'Confirmations', text: 'Sur chaque fiche, un bouton « Confirmé » permet à la communauté de valider qu’un bon plan est toujours d’actualité.' },
            { icon: Navigation, title: 'Itinéraire', text: 'Depuis votre position jusqu’au lieu choisi : distance et durée, après autorisation de la localisation.' },
            { icon: Banknote, title: 'Prix du moment', text: 'En plus des lieux, la communauté partage les prix constatés, pour savoir où et combien.' },
        ],
    },
    {
        slug: 'emissions',
        name: 'Émissions',
        icon: Mic,
        status: 'Réservé à l’équipe Vanessa pour le moment',
        tagline: 'Vanessa à voix haute, face à un invité.',
        intro:
            'Une émission est une conversation vocale où Vanessa anime un échange autour d’un sujet donné à l’avance. Pour l’instant, cet espace n’est ouvert qu’à l’équipe.',
        features: [
            { icon: Mic, title: 'Conversation vocale', text: 'Un micro, une discussion qui s’écrit en direct à côté : on peut suivre l’écrit pendant qu’on parle.' },
            { icon: Sparkles, title: 'Une posture au choix', text: 'Débat, discussion émotionnelle ou libre : on choisit comment Vanessa mène l’échange.' },
            { icon: Layers, title: 'Du sujet à la publication', text: 'Chaque émission passe par trois états : préparée, enregistrée, publiée.' },
        ],
        note: 'Vous êtes un média ou une organisation et ce format vous intéresse ? Écrivez-nous depuis la page Entreprises.',
    },
    {
        slug: 'application',
        name: 'Application',
        icon: Globe,
        tagline: 'Sur le web, sur ordinateur et téléphone.',
        intro:
            'Vanessa fonctionne dans votre navigateur, sur ordinateur comme sur téléphone. Il n’existe pas encore d’application à télécharger dans les boutiques : rien à installer, un compte suffit.',
        features: [
            { icon: Globe, title: 'Dans le navigateur', text: 'Ouvrez le site depuis votre téléphone ou votre ordinateur, vous retrouvez votre conversation avec Vanessa, Ça Parle et Ça sert.' },
            { icon: Users, title: 'Un seul compte', text: 'Le même compte sert pour l’application, ce site et l’API pour les développeurs.' },
            { icon: Bell, title: 'Notifications', text: 'Réactions, commentaires, messages et relances de Vanessa, configurables dans les réglages de votre appareil ou navigateur.' },
            { icon: MessageCircle, title: 'Messagerie', text: 'Écrivez en privé à l’auteur d’une histoire (si elle n’est pas anonyme) ou recherchez une personne.' },
        ],
        note: 'Une newsletter occasionnelle résume l’actualité de la plateforme. Un lien de désinscription figure dans chaque envoi.',
    },
];

export const getProduct = (slug?: string) => PRODUCTS.find((p) => p.slug === slug);
